import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CashToggle } from "@/components/CashToggle";
import { Loader2, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { tx, lz } from "@/i18n";

const PRIO = lz({ low: "Laag", normal: "Normaal", high: "Hoog" });
const NONE = "__none__";
const fromTask = (t) => ({
  title: t.title_original || t.title || "", description: t.description_original || t.description || "",
  house_id: t.house_id || NONE, assignee_id: t.assignee_id || NONE, due_date: t.due_date || "", due_time: t.due_time || "",
  priority: t.priority || "normal", status: t.status || "open",
  extra_cost: t.extra_cost != null ? String(t.extra_cost) : "", cash: !!t.cash,
  subtasks: (t.subtasks || []).map((s) => ({ name: s.name_original || s.name, done: !!s.done })),
});

function SubtaskEditor({ subs, onChange }) {
  const upd = (i, v) => onChange(subs.map((s, j) => (j === i ? { ...s, name: v } : s)));
  return (
    <div className="space-y-1.5" data-testid="task-edit-subtasks">
      {subs.map((s, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input value={s.name} onChange={(e) => upd(i, e.target.value)} className="h-8 text-sm" data-testid={`task-edit-subtask-${i}`} />
          <button type="button" onClick={() => onChange(subs.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive p-1" data-testid={`task-edit-subtask-remove-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...subs, { name: "", done: false }])} className="gap-1.5" data-testid="task-edit-subtask-add"><Plus className="h-4 w-4" /> {tx("Subtaak")}</Button>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label>{label}</Label><div className="mt-1">{children}</div></div>;
}

export function TaskEditDialog({ task, houses, staff, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setF(task ? fromTask(task) : null), [task]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, {
        ...f, house_id: f.house_id === NONE ? null : f.house_id, assignee_id: f.assignee_id === NONE ? null : f.assignee_id,
        due_date: f.due_date || null, due_time: f.due_date ? (f.due_time || null) : null, extra_cost: f.extra_cost === "" ? null : Number(f.extra_cost),
      });
      toast.success(tx("Taak bijgewerkt"));
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="task-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" /> {tx("Taak bewerken")}</DialogTitle>
          <DialogDescription className="sr-only">{tx("Taak bewerken")}</DialogDescription>
        </DialogHeader>
        {f && (
          <div className="space-y-3">
            <Field label={tx("Titel *")}><Input value={f.title} onChange={set("title")} data-testid="task-edit-title" /></Field>
            <Field label={tx("Beschrijving")}><Textarea value={f.description} onChange={set("description")} data-testid="task-edit-description" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tx("Woning")}>
                <Select value={f.house_id} onValueChange={set("house_id")}>
                  <SelectTrigger data-testid="task-edit-house"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>{tx("Geen")}</SelectItem>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={tx("Prioriteit")}>
                <Select value={f.priority} onValueChange={set("priority")}>
                  <SelectTrigger data-testid="task-edit-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tx("Toegewezen aan")}>
                <Select value={f.assignee_id} onValueChange={set("assignee_id")}>
                  <SelectTrigger data-testid="task-edit-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>{tx("Niemand")}</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {ROLE_LABELS[s.role]}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={tx("Datum")}><DatePicker value={f.due_date} onChange={set("due_date")} placeholder={tx("Geen datum")} testId="task-edit-due-date" /></Field>
            </div>
            {f.due_date && <Field label={tx("Tijd")}><Input type="time" value={f.due_time} onChange={set("due_time")} className="w-32" data-testid="task-edit-due-time" /></Field>}
            <div className="grid grid-cols-2 gap-3">
              <Field label={tx("Extra kosten (€)")}><Input type="number" min="0" step="0.01" value={f.extra_cost} onChange={set("extra_cost")} data-testid="task-edit-cost" /></Field>
              <Field label={tx("Status")}>
                <Select value={f.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="task-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="open">{tx("Open")}</SelectItem><SelectItem value="done">{tx("Afgerond")}</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex justify-end"><CashToggle value={f.cash} canEdit onToggle={() => set("cash")(!f.cash)} testId="task-edit-cash" /></div>
            <Field label={tx("Subtaken")}><SubtaskEditor subs={f.subtasks} onChange={set("subtasks")} /></Field>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="task-edit-cancel">{tx("Annuleren")}</Button>
          <Button onClick={save} disabled={saving || !f?.title.trim()} className="bg-primary hover:bg-primary/90" data-testid="task-edit-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
